import Vue from 'vue';
import {
  ENTITY_TYPES,
  setSnapshot,
  setSnapshots,
  removeSnapshot,
  clearSnapshots,
} from '../../../helpers/conflictTracker';
import { sortByPosition } from '../../../helpers/repairAssessmentOrder';
import {
  applyIncomingRowPreservingDirty,
  mergeRepairAssessmentPreservingDirty,
} from '../../../helpers/repairAssessmentDirtyMerge';

export default {
  SET_CURRENT_JOB(state, newJob) {
    const previousJobId = state.currentJob && state.currentJob._id;
    const newJobId = newJob && newJob._id;
    const isJobChange = previousJobId && previousJobId !== newJobId;
    if (isJobChange) {
      clearSnapshots(ENTITY_TYPES.RAT_ROW);
      Vue.set(state, 'editingFieldsByRow', {});
      Vue.set(state, 'deletedRepairAssessmentRowIds', {});
    }

    if (newJob && Array.isArray(newJob.repairAssessment)) {
      const incoming = sortByPosition(newJob.repairAssessment);
      const existing =
        !isJobChange &&
        state.currentJob &&
        Array.isArray(state.currentJob.repairAssessment)
          ? state.currentJob.repairAssessment
          : [];
      newJob.repairAssessment = mergeRepairAssessmentPreservingDirty(
        existing,
        incoming,
        state.editingFieldsByRow
      );
    }

    if (!state.currentJob || typeof state.currentJob !== 'object') {
      Vue.set(state, 'currentJob', newJob);
      if (newJob && Array.isArray(newJob.repairAssessment)) {
        setSnapshots(ENTITY_TYPES.RAT_ROW, newJob.repairAssessment);
      }
      return;
    }

    const target = state.currentJob;

    if (target && newJob && typeof newJob === 'object') {
      Object.keys(target).forEach((k) => {
        if (!(k in newJob)) Vue.delete(target, k);
      });
    }

    if (newJob && typeof newJob === 'object') {
      Object.keys(newJob).forEach((k) => {
        if (target[k] !== newJob[k]) Vue.set(target, k, newJob[k]);
      });
    }

    if (newJob && Array.isArray(newJob.repairAssessment)) {
      setSnapshots(ENTITY_TYPES.RAT_ROW, newJob.repairAssessment);
    }
  },

  SET_JOB_PROGRESS_TASKS: (state, tasks) => {
    state.jobProgressTasks = tasks;
  },

  UPSERT_REPAIR_ASSESSMENT_ROW(
    state,
    { incoming, forceFields = [], dirtyStamp }
  ) {
    if (!state.currentJob) return;
    if (!Array.isArray(state.currentJob.repairAssessment)) {
      Vue.set(state.currentJob, 'repairAssessment', []);
    }

    const list = state.currentJob.repairAssessment;
    const idx = list.findIndex((r) => r.id === incoming.id);

    if (idx === -1) {
      // A save started before the row was deleted can land afterwards; without
      // this guard it would push the deleted row back into the grid.
      if (state.deletedRepairAssessmentRowIds[incoming.id]) {
        return;
      }
      list.push(incoming);
      setSnapshot(ENTITY_TYPES.RAT_ROW, incoming);
      return;
    }

    const target = list[idx];
    const dirtyMap = state.editingFieldsByRow[incoming.id] || {};
    applyIncomingRowPreservingDirty(
      target,
      incoming,
      dirtyMap,
      forceFields,
      dirtyStamp
    );

    setSnapshot(ENTITY_TYPES.RAT_ROW, incoming);
  },

  MARK_FIELD_DIRTY(state, { rowId, field }) {
    if (!state.editingFieldsByRow[rowId])
      Vue.set(state.editingFieldsByRow, rowId, {});
    Vue.set(state.editingFieldsByRow[rowId], field, Date.now());
  },

  CLEAR_DIRTY_FIELDS(state, { rowId, fields, dirtyStamp }) {
    const dirty = state.editingFieldsByRow[rowId];
    if (dirty) {
      fields.forEach((f) => {
        if (dirtyStamp && dirty[f] !== dirtyStamp[f]) {
          return;
        }
        Vue.delete(dirty, f);
      });
      if (!Object.keys(dirty).length)
        Vue.delete(state.editingFieldsByRow, rowId);
    }
  },

  DELETE_REPAIR_ASSESSMENT_ROW(state, rowId) {
    Vue.set(state.deletedRepairAssessmentRowIds, rowId, true);
    Vue.delete(state.editingFieldsByRow, rowId);

    if (!state.currentJob || !Array.isArray(state.currentJob.repairAssessment))
      return;

    const idx = state.currentJob.repairAssessment.findIndex(
      (r) => r.id === rowId
    );
    if (idx !== -1) {
      state.currentJob.repairAssessment.splice(idx, 1);
    }
    removeSnapshot(ENTITY_TYPES.RAT_ROW, rowId);
  },

  SET_CURRENT_JOB_REPAIR_ASSESSMENT(state, repairAssessments) {
    // GET /ra reads the eventually consistent jobIdIndex GSI, so a refetch
    // right after a delete can still contain the removed rows.
    const sorted = sortByPosition(repairAssessments).filter(
      (row) => !state.deletedRepairAssessmentRowIds[row.id]
    );
    const existing = Array.isArray(state.currentJob.repairAssessment)
      ? state.currentJob.repairAssessment
      : [];
    const merged = mergeRepairAssessmentPreservingDirty(
      existing,
      sorted,
      state.editingFieldsByRow
    );
    Vue.set(state.currentJob, 'repairAssessment', merged);
    setSnapshots(ENTITY_TYPES.RAT_ROW, merged);
  },

  DELETE_REPORT_REVISION: (state, revision) => {
    state.currentJob.reports = state.currentJob.reports.filter(
      (rev) => rev.id !== revision.id
    );
  },

  SET_FAVOURITE_CURRENT_JOB(state, job) {
    state.currentJob = job;
  },

  UPDATE_FAVORITE_COUNT(state, { id, newCount }) {
    if (state.currentJob._id === id) {
      state.currentJob.favoriteCount = newCount;
    }
  },
};
